import React, { useEffect, useState } from "react";
import { api } from "@/context/AuthContext";
import { useAuth } from "@/context/AuthContext";
import { Zap, Users, GitBranch, Save, Info, RefreshCw, CheckCircle } from "lucide-react";

const MODE_OPTIONS = [
  {
    key: "manual",
    label: "Manual",
    icon: Users,
    description: "All inspections require manual assignment by the manager. No automatic dispatch occurs.",
    color: "border-slate-200 text-slate-600",
    active: "border-navy bg-navy/5 text-navy",
  },
  {
    key: "auto",
    label: "Auto",
    icon: Zap,
    description: "Inspections with risk score below the threshold are automatically dispatched to the matching territory rep.",
    color: "border-slate-200 text-slate-600",
    active: "border-green-600 bg-green-50 text-green-700",
  },
  {
    key: "hybrid",
    label: "Hybrid",
    icon: GitBranch,
    description: "Auto-dispatch for routine cases (below threshold). Manager reviews high-risk cases manually.",
    color: "border-slate-200 text-slate-600",
    active: "border-amber-500 bg-amber-50 text-amber-700",
  },
];

export default function DispatchSettingsTab({ isAdmin }) {
  const { user } = useAuth();
  const [settings, setSettings] = useState({ mode: "manual", threshold: 65, enabled: false });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);

  useEffect(() => {
    const load = async () => {
      try {
        const res = await api.get("/lc/dispatch-settings");
        setSettings(res.data);
      } catch {}
      setLoading(false);
    };
    load();
  }, []);

  const handleSave = async () => {
    setSaving(true);
    try {
      await api.put("/lc/dispatch-settings", settings);
      setSaved(true);
      setTimeout(() => setSaved(false), 2000);
    } catch {}
    setSaving(false);
  };

  const canEditMode = isAdmin || user?.role === "admin";

  if (loading) return (
    <div className="py-16 text-center text-slate-400">
      <RefreshCw className="w-5 h-5 animate-spin mx-auto mb-2" />
    </div>
  );

  const currentMode = MODE_OPTIONS.find(m => m.key === settings.mode) || MODE_OPTIONS[0];

  return (
    <div className="space-y-8 max-w-2xl">
      <div>
        <h3 className="font-heading font-bold text-slate-800 text-base mb-1">Dispatch Configuration</h3>
        <p className="text-sm text-slate-500">
          Control how completed surveys are routed to field representatives.
        </p>
      </div>

      {/* Dispatch Mode */}
      <div>
        <label className="block text-xs font-semibold text-slate-500 uppercase tracking-wider mb-3">
          Dispatch Mode
          {!canEditMode && <span className="ml-2 text-slate-400 normal-case font-normal">(set by carrier admin)</span>}
        </label>
        <div className="grid grid-cols-3 gap-3">
          {MODE_OPTIONS.map(opt => {
            const isActive = settings.mode === opt.key;
            const disabled = !canEditMode;
            return (
              <button
                key={opt.key}
                data-testid={`dispatch-mode-${opt.key}`}
                onClick={() => !disabled && setSettings(p => ({ ...p, mode: opt.key }))}
                disabled={disabled}
                className={`relative p-4 rounded-xl border-2 text-left transition-all ${
                  isActive ? opt.active : `${opt.color} hover:border-slate-300`
                } ${disabled ? "opacity-60 cursor-not-allowed" : "cursor-pointer"}`}
              >
                <opt.icon className="w-5 h-5 mb-2" />
                <p className="font-semibold text-sm">{opt.label}</p>
                <p className="text-xs mt-1 leading-relaxed opacity-75">{opt.description}</p>
                {isActive && (
                  <span className="absolute top-2 right-2">
                    <CheckCircle className="w-4 h-4" />
                  </span>
                )}
              </button>
            );
          })}
        </div>
      </div>

      {/* Risk Threshold Slider */}
      {settings.mode !== "manual" && (
        <div>
          <label className="block text-xs font-semibold text-slate-500 uppercase tracking-wider mb-3">
            Risk Score Threshold
          </label>

          <div className="bg-white border border-slate-200 rounded-xl p-5 space-y-4">
            {/* Visual bar */}
            <div className="relative h-6 rounded-full overflow-hidden bg-slate-100">
              <div
                className="absolute left-0 top-0 h-full bg-green-400 transition-all duration-200"
                style={{ width: `${settings.threshold}%` }}
              />
              <div
                className="absolute top-0 h-full bg-amber-400 transition-all duration-200"
                style={{ left: `${settings.threshold}%`, right: 0 }}
              />
              <div
                className="absolute top-0 bottom-0 w-0.5 bg-white z-10 transition-all duration-200"
                style={{ left: `${settings.threshold}%` }}
              />
            </div>

            <div className="flex items-center justify-between text-xs text-slate-500">
              <span className="flex items-center gap-1">
                <span className="w-2 h-2 rounded-full bg-green-400"></span>
                Auto-dispatch zone (0 – {settings.threshold})
              </span>
              <span className="flex items-center gap-1">
                <span className="w-2 h-2 rounded-full bg-amber-400"></span>
                Manual decision zone ({settings.threshold} – 100)
              </span>
            </div>

            <input
              type="range"
              min={0}
              max={100}
              step={5}
              value={settings.threshold}
              onChange={e => setSettings(p => ({ ...p, threshold: Number(e.target.value) }))}
              className="w-full accent-navy"
              data-testid="threshold-slider"
            />

            <div className="text-center">
              <span className="inline-block bg-navy text-white text-sm font-bold px-4 py-1.5 rounded-full" data-testid="threshold-value">
                Threshold: {settings.threshold}
              </span>
            </div>

            {/* Explanation */}
            <div className="bg-slate-50 rounded-lg p-4 space-y-2 text-xs text-slate-600">
              <div className="flex items-start gap-2">
                <span className="w-2 h-2 rounded-full bg-green-400 mt-1 flex-shrink-0"></span>
                <p><strong>Risk score ≤ {settings.threshold}:</strong> Inspection is automatically dispatched to the territory rep covering the insured's state.</p>
              </div>
              <div className="flex items-start gap-2">
                <span className="w-2 h-2 rounded-full bg-amber-400 mt-1 flex-shrink-0"></span>
                <p><strong>Risk score &gt; {settings.threshold}:</strong> Manager is presented with two options — <em>Complete (no site visit needed)</em> or <em>Assign to rep</em> for on-site investigation.</p>
              </div>
            </div>
          </div>
        </div>
      )}

      {settings.mode === "manual" && (
        <div className="flex items-start gap-3 p-4 bg-slate-50 rounded-xl border border-slate-200">
          <Info className="w-4 h-4 text-slate-400 mt-0.5 flex-shrink-0" />
          <p className="text-sm text-slate-600">
            In Manual mode, the risk score threshold is not used. All completed surveys require the manager to manually assign a field rep or mark them complete.
          </p>
        </div>
      )}

      {/* Module enable toggle */}
      <div className="flex items-center justify-between p-4 bg-slate-50 rounded-xl border border-slate-200">
        <div>
          <p className="font-semibold text-slate-700 text-sm">Enable LC Dispatch Module</p>
          <p className="text-xs text-slate-500 mt-0.5">Activates dispatch triggers when surveys are completed (Sprint 3)</p>
        </div>
        <button
          data-testid="toggle-lc-enabled"
          onClick={() => canEditMode && setSettings(p => ({ ...p, enabled: !p.enabled }))}
          disabled={!canEditMode}
          className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors focus:outline-none ${
            settings.enabled ? "bg-navy" : "bg-slate-300"
          } ${!canEditMode ? "opacity-60 cursor-not-allowed" : "cursor-pointer"}`}
        >
          <span className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${settings.enabled ? "translate-x-6" : "translate-x-1"}`} />
        </button>
      </div>

      {/* Save */}
      <div className="flex items-center gap-3">
        <button
          onClick={handleSave}
          disabled={saving}
          className="flex items-center gap-2 bg-navy text-white px-5 py-2.5 rounded-lg text-sm font-medium hover:bg-navy/90 disabled:opacity-60 transition-colors"
          data-testid="save-dispatch-settings-btn"
        >
          {saving ? <RefreshCw className="w-4 h-4 animate-spin" /> : saved ? <CheckCircle className="w-4 h-4" /> : <Save className="w-4 h-4" />}
          {saving ? "Saving..." : saved ? "Saved!" : "Save Settings"}
        </button>
        {!canEditMode && (
          <p className="text-xs text-slate-400">Contact your carrier admin to change the dispatch mode</p>
        )}
      </div>
    </div>
  );
}
